import { useId } from 'react';
import type { Lang } from '../../i18n/index.tsx';

/**
 * A language's flag, drawn as SVG (emoji flags do not render on Windows): the United States for English, Japan,
 * South Korea. Every flag fills the same 3:2 box with rounded corners, so the menu's rows line up.
 */
export function Flag({ lang, width = 22 }: { lang: Lang; width?: number }) {
  const id = useId().replace(/:/g, '');
  const height = Math.round((width * 2) / 3);
  return (
    <svg className="flag" width={width} height={height} viewBox="0 0 60 40" preserveAspectRatio="xMidYMid slice" aria-hidden="true" data-flag={lang}>
      <clipPath id={`fr${id}`}>
        <rect width="60" height="40" rx="5" />
      </clipPath>
      <g clipPath={`url(#fr${id})`}>{lang === 'ja' ? <Japan /> : lang === 'ko' ? <Korea id={id} /> : <UnitedStates />}</g>
      <rect width="60" height="40" rx="5" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="1.2" />
    </svg>
  );
}

function UnitedStates() {
  // 13 stripes (red first), the blue canton over the top 7 at 40% of the width, its 50 stars in 9 staggered rows
  const stripe = 40 / 13;
  const stars: [number, number][] = [];
  for (let r = 0; r < 9; r++) {
    const n = r % 2 === 0 ? 6 : 5;
    for (let c = 0; c < n; c++) stars.push([(r % 2 === 0 ? 2 : 4) + c * 4, 1.9 + r * 2.15]);
  }
  return (
    <>
      <rect width="60" height="40" fill="#fff" />
      {Array.from({ length: 7 }, (_, i) => (
        <rect key={i} y={i * 2 * stripe} width="60" height={stripe} fill="#B22234" />
      ))}
      <rect width="24" height={7 * stripe} fill="#3C3B6E" />
      {stars.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="0.75" fill="#fff" />
      ))}
    </>
  );
}

function Japan() {
  return (
    <>
      <rect width="60" height="40" fill="#fff" />
      <circle cx="30" cy="20" r="12" fill="#BC002D" />
    </>
  );
}

function Korea({ id }: { id: string }) {
  // the official construction (72 × 48 units), scaled into the 60 × 40 box
  const bars = `kb${id}`;
  return (
    <g transform="translate(30 20) scale(0.8333)">
      <rect x="-36" y="-24" width="72" height="48" fill="#fff" />
      <defs>
        <path id={bars} d="M-6-25H6M-6-22H6M-6-19H6" stroke="#000" strokeWidth="2" />
      </defs>
      <g transform="rotate(-56.31)">
        <use href={`#${bars}`} />
        <use href={`#${bars}`} y="44" />
        <path d="M0,17v10" stroke="#fff" strokeWidth="1" />
        <circle r="12" fill="#CD2E3A" />
        <path d="M0-12A6,6 0 0 0 0,0A6,6 0 0 1 0,12A12,12 0 0 1 0-12Z" fill="#0047A0" />
      </g>
      <g transform="rotate(-123.69)">
        <use href={`#${bars}`} />
        <use href={`#${bars}`} y="44" />
        <path d="M0-23.5v3M0,17v3.5M0,23.5v3" stroke="#fff" strokeWidth="1" />
      </g>
    </g>
  );
}
